<script setup lang="ts">
    import type { Asset } from '~/@types/cms';

    export interface SharedImageProps {
        /** single string source for image  */
        src?: string;
        /** data Asset from CMS */
        data?: Asset;
        height?: string | number | undefined;
        width?: string | number | undefined;
        renderAs?: 'img' | 'picture';
        preset?: '16to9' | '3to2' | '2to3' | '1to1';
        noTransform?: boolean;
        cover?: boolean;
        sizes?: string;
        /** [Docs 📘](https://image.nuxt.com/usage/nuxt-img#preload) */
        imagePreload?: boolean;
        /** [Docs 📘](https://image.nuxt.com/usage/nuxt-img#loading) */
        imageLoading?: 'eager' | 'lazy';
        uspTitle?: string;
        gtmClick?: string;
    }

    export interface ImageProps extends SharedImageProps {
        contain?: boolean;
        types?: string[];
        modifiers?: any;
        alt?: string;
    }

    const props = withDefaults(defineProps<ImageProps>(), {
        renderAs: 'picture',
        modifiers: {},
        sizes: undefined,
        preset: undefined,
        alt: '',
        placeholder: false,
        imagePreload: false,
        imageLoading: 'lazy',
    });

    const emit = defineEmits(['error']);

    const imageLoaded = ref(false);

    const handleError = () => {
        emit('error');
        imageLoaded.value = true;
    };

    // props need to be reactive in oder for hover color to work
    const reactiveProps = toRefs(props);
    const assetInfo = computed(() =>
        getAssetInfo({
            data: reactiveProps.data.value,
            src: reactiveProps.src.value,
            preset: reactiveProps.preset.value,
            noTransform: reactiveProps.noTransform.value,
        }),
    );
    const isPng = computed(() => assetInfo.value.src?.endsWith('.png'));
</script>
<template>
    <nuxt-picture
        v-if="assetInfo.src && renderAs === 'picture'"
        :src="assetInfo.src"
        :provider="assetInfo.provider"
        :format="assetInfo.provider === 'kentico' ? 'webp,jpg' : assetInfo.provider === 'shpfy' ? 'png' : undefined"
        :alt="alt"
        :width="width"
        :height="height"
        :preload="imagePreload"
        :loading="imageLoading"
        :modifiers="{ fit: 'cover', preset }"
        :img-attrs="{
            // @ts-expect-error https://github.com/nuxt/image/issues/2013
            'data-usp-title': uspTitle,
            class: [
                gtmClick ? gtmClick : '',
                cover ? 'w-full h-full object-cover object-center bg-cover bg-no-repeat bg-center' : 'bg-cover bg-no-repeat bg-center',
            ],
            style: { backgroundImage: !isPng && !imageLoaded && assetInfo.base64Image ? `url('${assetInfo.base64Image}')` : undefined },
        }"
        :sizes="sizes"
        @load="() => (imageLoaded = true)"
        @error="handleError" />
    <nuxt-img
        v-else-if="assetInfo.src"
        :src="assetInfo.src"
        :provider="assetInfo.provider"
        :alt="alt"
        :width="width"
        :height="height"
        :preload="imagePreload"
        :loading="imageLoading"
        :class="[gtmClick ? gtmClick : '', { 'animate-pulse': !imageLoaded }]"
        :modifiers="{ fit: cover ? 'cover' : contain ? 'contain' : undefined, ...modifiers, preset }"
        :sizes="sizes"
        :data-usp-title="uspTitle"
        :placeholder="assetInfo.base64Image"
        @load="() => (imageLoaded = true)"
        @error="handleError" />
</template>
